import React, { useMemo, useState } from 'react';
import { PiArrowRight, PiMapPinPlus, PiPencilSimple } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { LocationMapThumbnail } from 'src/modules/shared/components/molecules/location-map-thumbnail';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { buildLocationCountryOptions } from 'src/modules/shared/utils/location/country.util';
import type {
  PropertyLocationFieldsProps,
  PropertyLocationValues,
} from '../../types/property-location-fields.type';
import { propertyLocationFieldNames } from '../../utils/property-location-field.util';
import { parsePropertyCoordinate } from '../../utils/property-location.util';
import { PropertyLocationModal } from './property-location-modal';
import { PropertyEditorSectionCard } from './property-editor-section-card';

/** Address summary and map preview whose modal only updates the page draft. */
export const PropertyLocationFields: React.FC<PropertyLocationFieldsProps> = ({
  register,
  setValue,
  disabled,
  tenantCountryCode,
  values,
}) => {
  const { t, language } = useI18n();
  const [editing, setEditing] = useState<PropertyLocationValues | null>(null);
  const country = useMemo(
    () =>
      buildLocationCountryOptions(language, values.countryCode).find(
        (option) => option.value === values.countryCode
      )?.content,
    [language, values.countryCode]
  );
  const latitude = parsePropertyCoordinate(values.latitude);
  const longitude = parsePropertyCoordinate(values.longitude);
  const hasPoint = latitude != null && longitude != null;
  const street = [values.streetName, values.streetNumber]
    .filter(Boolean)
    .join(', ');
  const geography = [
    values.neighborhood,
    [values.locality, values.region].filter(Boolean).join(', '),
    country,
  ]
    .filter(Boolean)
    .join(' · ');
  const addressTitle =
    [street, values.addressDisplay].find((value) => value?.trim()) ??
    t('catalog.properties.location.noAddress');
  const hasAddress =
    hasPoint ||
    [
      street,
      values.addressDisplay,
      values.locality,
      values.region,
      values.neighborhood,
    ].some((value) => Boolean(value?.trim()));
  const open = () => setEditing({ ...values });
  const apply = (next: PropertyLocationValues) => {
    for (const name of propertyLocationFieldNames) {
      if ((next[name] ?? '') !== (values[name] ?? '')) {
        setValue(name, next[name] ?? '', {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    }
    setEditing(null);
  };
  return (
    <>
      {propertyLocationFieldNames.map((name) => (
        <input key={name} type="hidden" {...register(name)} />
      ))}
      <PropertyEditorSectionCard
        title={t('catalog.properties.editor.sections.location')}
        description={t(
          'catalog.properties.editor.sections.locationDescription'
        )}
        actions={
          <Button
            type="button"
            variant="neutral"
            appearance="outline"
            size="sm"
            disabled={disabled}
            onClick={open}
          >
            <PiPencilSimple aria-hidden="true" className="h-4 w-4" />
            {t(
              hasAddress
                ? 'catalog.properties.table.actions.edit'
                : 'catalog.properties.location.add'
            )}
          </Button>
        }
      >
        <div className="min-w-0 space-y-5">
          <div className="space-y-1">
            <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-2">
              <Text weight="medium" className="break-words">
                {addressTitle}
              </Text>
              {values.unit ? (
                <Text size="small" className="text-neutral-600">
                  <span aria-hidden="true" className="hidden sm:inline">
                    ·{' '}
                  </span>
                  {t('catalog.properties.location.unitSummary')} {values.unit}
                </Text>
              ) : null}
            </div>
            {hasAddress && geography ? (
              <Text size="small" className="text-neutral-600">
                {geography}
              </Text>
            ) : null}
          </div>
          {values.addressDisplay ? (
            <div className="space-y-1">
              <Text size="tiny" className="text-neutral-600">
                {t('catalog.properties.form.addressDisplay')}
              </Text>
              <Text size="small" className="break-words">
                {values.addressDisplay}
              </Text>
            </div>
          ) : null}
        </div>
        <Button
          type="button"
          variant="neutral"
          appearance="soft"
          disabled={disabled}
          onClick={open}
          aria-label={t(
            hasPoint
              ? 'catalog.properties.location.adjustMap'
              : 'catalog.properties.location.placeOnMap'
          )}
          className="!h-auto w-full !justify-between !whitespace-normal !rounded-xl !p-4 text-left"
        >
          <span className="flex min-w-0 items-center gap-3">
            {hasPoint ? (
              <LocationMapThumbnail latitude={latitude} longitude={longitude} />
            ) : (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white">
                <PiMapPinPlus aria-hidden="true" className="h-5 w-5" />
              </span>
            )}
            <span className="min-w-0">
              <Text as="span" size="small" weight="medium" className="block">
                {t(
                  hasPoint
                    ? 'catalog.properties.location.adjustMap'
                    : 'catalog.properties.location.placeOnMap'
                )}
              </Text>
              <Text
                as="span"
                size="tiny"
                className="mt-0.5 block text-neutral-600"
              >
                {t(
                  hasPoint
                    ? 'catalog.properties.location.mapStripSetHelp'
                    : 'catalog.properties.location.mapStripHelp'
                )}
              </Text>
            </span>
          </span>
          <PiArrowRight aria-hidden="true" className="h-5 w-5 shrink-0" />
        </Button>
      </PropertyEditorSectionCard>
      {editing && !disabled ? (
        <PropertyLocationModal
          initialValues={editing}
          tenantCountryCode={tenantCountryCode}
          disabled={disabled}
          onClose={() => setEditing(null)}
          onApply={apply}
        />
      ) : null}
    </>
  );
};
